import { Controller } from "@hotwired/stimulus";

/**
 * PreloaderController
 *
 * Page-load preloader con SVG water fill (máscara del logo ÖKO).
 * Animación adaptada de material/index.html — timing, easing y onda
 * sinusoidal con 3 frecuencias sobre la silueta del logo.
 *
 * Timing (replicado de material/index.html):
 *   800ms  — pausa inicial mostrando el contorno vacío
 *   2800ms — llenado del aro de la Ö (easeInOutCubic)
 *   1200ms — llenado de los puntos / desborde (easeOutCubic)
 *   ~300ms — pausa antes de fundir el outline
 *   Total animación: ~4800ms
 *
 * La animación SIEMPRE corre su ciclo completo (no se acelera con
 * DOMContentLoaded). Solo completa cuando:
 *   a) El ciclo de animación terminó, Y
 *   b) window.load ya se disparó (o prefers-reduced-motion)
 *
 * Timeout de seguridad: 5000ms.
 *
 * Dispara evento `preloader:done` al finalizar.
 *
 * Estados: idle → animating → ready → done → removed
 */
export default class extends Controller {
	connect() {
		// Referencias SVG
		this._waterPath = this.element.querySelector("#oko-water-path");
		this._outlineGroup = this.element.querySelector("#oko-outline-group");
		this._innerHint = this.element.querySelector("#oko-inner-hint");

		// Timing de la animación (rápido)
		this._DELAY = 200;
		this._FILL_O_DURATION = 800;
		this._FILL_DOTS_DURATION = 400;
		this._FADE_DELAY = 100;

		this._startTime = performance.now();
		this._duration = 2500; // timeout máximo
		this._rafId = null;
		this._done = false;
		this._ready = false; // true cuando window.load ya disparó
		this._reducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;

		// Estado de la onda
		this._phase = 0;

		// Constantes geométricas del logo (viewBox 512×512)
		this._O_TOP = 112;
		this._DOT_TOP = 33;
		this._WATER_BASE = 560;

		// Si ya cargó completamente al conectarse
		if (document.readyState === "complete") {
			this._ready = true;
		} else {
			// Solo esperamos window.load — NO aceleramos la animación
			this._onWindowLoad = () => {
				this._ready = true;
			};
			window.addEventListener("load", this._onWindowLoad);
		}

		// Reduced motion: completar inmediatamente
		if (this._reducedMotion) {
			this._complete();
			return;
		}

		// Dibujar estado inicial (vacío con outline visible)
		this._initWater();

		// Arrancar RAF loop
		this._tick();
	}

	disconnect() {
		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
			this._rafId = null;
		}
		window.removeEventListener("load", this._onWindowLoad);
	}

	// ------------------------------------------------------------------
	// Easing
	// ------------------------------------------------------------------

	_easeInOutCubic(t) {
		return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
	}

	_easeOutCubic(t) {
		return 1 - (1 - t) ** 3;
	}

	// ------------------------------------------------------------------
	// RAF loop
	// ------------------------------------------------------------------

	_tick() {
		if (this._done) return;

		const elapsed = performance.now() - this._startTime;

		// Animar siempre el agua con su timing completo
		this._updateWater(elapsed);

		// El ciclo de animación terminó?
		const animCycleDone =
			elapsed >=
			this._DELAY +
				this._FILL_O_DURATION +
				this._FILL_DOTS_DURATION +
				this._FADE_DELAY;

		// Timeout de seguridad (5000ms)
		const timedOut = elapsed >= this._duration;

		if ((animCycleDone || timedOut) && this._ready) {
			this._complete();
		} else {
			this._rafId = requestAnimationFrame(() => this._tick());
		}
	}

	// ------------------------------------------------------------------
	// Water fill via SVG path (timing de material/index.html)
	// ------------------------------------------------------------------

	_initWater() {
		if (!this._waterPath) return;
		// Agua fuera del viewBox (invisible)
		const d = this._buildWaterPath(this._WATER_BASE, 0, 0, 0, 0);
		this._waterPath.setAttribute("d", d);

		// Asegurar outline visible inicialmente
		if (this._outlineGroup) this._outlineGroup.classList.remove("is-filled");
		if (this._innerHint) this._innerHint.classList.remove("is-filled");
	}

	/**
	 * Actualiza el path del agua según el tiempo transcurrido.
	 * Timing idéntico a material/index.html:
	 *   DELAY → FILL_O → FILL_DOTS → FADE_DELAY → outline fade
	 */
	_updateWater(elapsed) {
		if (!this._waterPath) return;

		// Tiempo efectivo después del delay inicial
		const effectiveElapsed = elapsed - this._DELAY;

		// Nivel del agua (replicado de material/index.html)
		const waterY = this._getWaterY(effectiveElapsed);

		// Onda sinusoidal
		this._phase += 0.032;
		const ampFactor = this._getAmplitudeFactor(waterY);
		const amp1 = 10 * ampFactor;
		const amp2 = 5 * ampFactor;
		const amp3 = 2 * ampFactor;

		const d = this._buildWaterPath(waterY, this._phase, amp1, amp2, amp3);
		this._waterPath.setAttribute("d", d);

		// Fundido del outline cuando la animación del agua terminó
		const fillComplete = this._FILL_O_DURATION + this._FILL_DOTS_DURATION;
		if (effectiveElapsed > fillComplete + this._FADE_DELAY) {
			if (this._outlineGroup) this._outlineGroup.classList.add("is-filled");
			if (this._innerHint) this._innerHint.classList.add("is-filled");
		} else {
			if (this._outlineGroup) this._outlineGroup.classList.remove("is-filled");
			if (this._innerHint) this._innerHint.classList.remove("is-filled");
		}
	}

	/**
	 * Calcula la posición Y del agua según el tiempo efectivo.
	 * Replicado de material/index.html:
	 *   elapsed < 0       → WATER_BASE (en delay)
	 *   < FILL_O          → llenando el aro (easeInOutCubic)
	 *   < FILL_O + DOTS   → llenando puntos (easeOutCubic)
	 *   >= completo       → DOT_TOP - 8 (totalmente lleno)
	 */
	_getWaterY(elapsed) {
		if (elapsed < 0) return this._WATER_BASE;

		if (elapsed < this._FILL_O_DURATION) {
			const t = elapsed / this._FILL_O_DURATION;
			const eased = this._easeInOutCubic(t);
			return this._WATER_BASE - (this._WATER_BASE - this._O_TOP) * eased;
		}

		const afterO = elapsed - this._FILL_O_DURATION;
		if (afterO < this._FILL_DOTS_DURATION) {
			const t = afterO / this._FILL_DOTS_DURATION;
			const eased = this._easeOutCubic(t);
			return this._O_TOP - (this._O_TOP - this._DOT_TOP) * eased;
		}

		return this._DOT_TOP - 8;
	}

	/**
	 * Factor de amplitud: 1.0 mientras llena el aro,
	 * se reduce progresivamente al llenar los puntos.
	 */
	_getAmplitudeFactor(waterY) {
		if (waterY >= this._O_TOP) return 1.0;
		const range = this._O_TOP - (this._DOT_TOP - 8);
		const progress = Math.max(0, Math.min(1, (this._O_TOP - waterY) / range));
		return 1 - progress * 0.65;
	}

	/**
	 * Construye el path SVG del agua con superficie ondulada.
	 * Es un polígono cerrado: onda arriba, desciende, base horizontal, cierra.
	 */
	_buildWaterPath(waterY, phase, amp1, amp2, amp3) {
		const WAVE_FREQ = 0.025;
		const WAVE_FREQ_2 = 0.055;
		const WAVE_FREQ_3 = 0.11;
		const STEP = 48;
		const WIDTH = 512;
		const MARGIN = 20;
		const endX = WIDTH + MARGIN;
		const BASE_Y = 560;

		const wave = (x) =>
			amp1 * Math.sin(x * WAVE_FREQ + phase) +
			amp2 * Math.sin(x * WAVE_FREQ_2 + phase * 1.7) +
			amp3 * Math.sin(x * WAVE_FREQ_3 + phase * 2.3);

		let d = "";

		for (let x = -MARGIN; x <= endX; x += STEP) {
			const xNext = Math.min(x + STEP, endX);
			const midX = (x + xNext) / 2;

			const y = waterY + wave(x);
			const yn = waterY + wave(xNext);
			const ym = waterY + wave(midX);

			if (x === -MARGIN) {
				d = `M ${x.toFixed(1)},${y.toFixed(1)}`;
			} else {
				d += ` Q ${midX.toFixed(1)},${ym.toFixed(1)} ${xNext.toFixed(1)},${yn.toFixed(1)}`;
			}
		}

		// Cerrar el polígono por debajo del viewBox
		d += ` L ${endX.toFixed(1)},${(BASE_Y + 50).toFixed(1)}`;
		d += ` L ${(-MARGIN).toFixed(1)},${(BASE_Y + 50).toFixed(1)}`;
		d += " Z";

		return d;
	}

	// ------------------------------------------------------------------
	// Complete
	// ------------------------------------------------------------------

	/**
	 * Completa la animación: agua llena, outline fundido, fade out,
	 * evento, remoción del DOM.
	 */
	_complete() {
		if (this._done) return;
		this._done = true;

		// Cancelar RAF
		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
			this._rafId = null;
		}

		// Remover listener
		window.removeEventListener("load", this._onWindowLoad);

		// Forzar agua llena y outline fundido
		if (!this._reducedMotion) {
			const d = this._buildWaterPath(this._DOT_TOP - 8, 0, 0, 0, 0);
			if (this._waterPath) this._waterPath.setAttribute("d", d);
		}
		if (this._outlineGroup) this._outlineGroup.classList.add("is-filled");
		if (this._innerHint) this._innerHint.classList.add("is-filled");

		// Fade out
		this.element.classList.add("oko-preloader--hidden");

		// Disparar evento para controllers GSAP/ScrollTrigger
		document.dispatchEvent(
			new CustomEvent("preloader:done", {
				detail: {
					timestamp: Date.now(),
					duration: Math.round(performance.now() - this._startTime),
				},
			}),
		);

		// Remover del DOM después de la transición CSS
		setTimeout(() => {
			if (this.element.parentNode) {
				this.element.parentNode.removeChild(this.element);
			}
			if (typeof window.ScrollTrigger !== "undefined") {
				window.ScrollTrigger.refresh();
			}
		}, 700);
	}
}
